import { TProduct } from 'widgets/product/ProductDetail';
import { TProductDeliveryOptionsInstance } from './ProductDeliveryOptions';
import { validatePersonalizationText } from './PersonalizationDrawer';
import { TPersonalizationConfig, TPersonalizationSummaryInstance } from './PersonalizationSummary';
type TPersonalizationProduct = TProduct & {
    isFinalSale?: boolean;
    isPersonalizable?: boolean;
    personalization?: {
        images?: Array<Record<string, unknown>>;
    } | null;
};

/**
 * @description PersonalizationMixin implementation
 * @param ProductDetail Base widget for extending
 * @returns Personalization mixin class
 */
function PersonalizationMixinClassCreator(ProductDetail: import('widgets/product/ProductDetail').TProductDetail) {
    class PersonalizationMixin extends ProductDetail {
        personalizationText = '';

        /**
         * @description Get the personalization summary row widget
         * @returns summary widget or undefined if the product has no PDP personalization experience
         */
        getPersonalizationSummary(): TPersonalizationSummaryInstance | undefined {
            return this.getById<TPersonalizationSummaryInstance, TPersonalizationSummaryInstance>('personalizationSummary', summary => summary);
        }

        /**
         * @description Get the personalization configuration of the current product
         * @returns configuration or null if the current product cannot be personalized on the PDP
         */
        getPersonalizationConfig(): TPersonalizationConfig | null {
            const summary = this.getPersonalizationSummary();

            return summary?.isShown() ? summary.getConfig() : null;
        }

        /**
         * @description Check whether the active personalization text breaks the rules
         * @param config - personalization configuration
         * @returns true if the product is personalized with a not valid text
         */
        isPersonalizationInvalid(config: TPersonalizationConfig): boolean {
            return !!this.personalizationText && !!validatePersonalizationText(this.personalizationText, config);
        }

        /**
         * @description Personalization summary change handler, see `data-forward-to-parent` of the summary
         * @param text - personalization text, empty if the personalization was removed
         * @emits "product.personalization.changed"
         */
        onPersonalizationChange(text: string): void {
            this.personalizationText = text;
            this.syncPersonalizationOption();
            this.renderFinalSaleMessage(this.product);
            this.updatePickupInStore();
            this.emitSelectedOptions();
        }

        /**
         * @description Notify the express checkout buttons about the selected options
         * @emits "product.personalization.changed"
         */
        emitSelectedOptions(): void {
            /**
             * @description Event to notify the express checkout buttons about the selected options
             * @event "product.personalization.changed"
             */
            this.eventBus().emit('product.personalization.changed', this.getSelectedProductOptions(), this);
        }

        /**
         * @description Reflect the active personalization in the `productOptions` preference,
         * so every consumer of the preference (e.g. add to wishlist) gets the same selection
         */
        syncPersonalizationOption(): void {
            const config = this.getPersonalizationConfig();

            if (!config) {
                return;
            }

            const text = this.personalizationText;
            const productOptions = (this.prefs().productOptions || []).map(option => {
                if (option.id !== config.optionId) {
                    return option;
                }

                return {
                    ...option,
                    selectedValueId: text ? config.customValueId : config.defaultValueId,
                    customOptionValue: text || null
                };
            });

            this.setPref('productOptions', productOptions);
        }

        /**
         * @description Get custom option value
         * @param optionId Product option ID
         * @returns custom option value
         */
        getCustomOptionValue(optionId: string): void | string {
            const config = this.getPersonalizationConfig();

            if (config?.optionId === optionId) {
                return this.personalizationText;
            }

            return super.getCustomOptionValue(optionId);
        }

        /**
         * @description Validate custom field and return its validation result
         * @param optionId Product option ID
         * @returns field validation result
         */
        isCustomOptionValid(optionId: string): void | boolean {
            const config = this.getPersonalizationConfig();

            if (config?.optionId === optionId) {
                return !this.isPersonalizationInvalid(config);
            }

            return super.isCustomOptionValid(optionId);
        }

        /**
         * @description Render product options. The personalization option has no option selector to render,
         * so the `productOptions` preference is updated here and the active personalization is kept
         * unless the selected product cannot be personalized, e.g. its color is excluded from personalization
         * @param product Product object
         */
        renderProductOptions(product: TPersonalizationProduct): void {
            super.renderProductOptions(product);

            const summary = this.getPersonalizationSummary();

            if (!summary) {
                return;
            }

            this.setPref('productOptions', product.options || []);

            const isPersonalizable = !!product.isPersonalizable;

            if (!isPersonalizable) {
                summary.reset();
            }

            if (product.personalization?.images) {
                summary.setImages(product.personalization.images);
            }

            summary.toggle(isPersonalizable);
            this.syncPersonalizationOption();
            this.emitSelectedOptions();
        }

        /**
         * @description Hide the pick up in store option for the personalized product
         */
        updatePickupInStore(): void {
            const isPickupRestricted = !!this.personalizationText && !this.getPersonalizationConfig()?.bopisEnabled;

            this.getById('productDeliveryOptions', (deliveryOptions: TProductDeliveryOptionsInstance) => {
                deliveryOptions.togglePickupRestriction(isPickupRestricted).catch(() => undefined);
            });
        }

        /**
         * @description Render final sale message, the personalized product is always final sale
         * @param product Product object
         */
        renderFinalSaleMessage(product: TPersonalizationProduct): void {
            if (!product) {
                return;
            }

            const isProductFinalSale = 'isFinalSale' in product ? !!product.isFinalSale : !!this.getPersonalizationConfig()?.isFinalSale;

            super.renderFinalSaleMessage({
                ...product,
                isFinalSale: isProductFinalSale || !!this.personalizationText
            });
        }
    }

    return PersonalizationMixin;
}

export type TPersonalizationMixin = ReturnType<typeof PersonalizationMixinClassCreator>;

export type TPersonalizationMixinInstance = InstanceType<TPersonalizationMixin>;

export default PersonalizationMixinClassCreator;
